import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Heading,
  Text,
  Button,
  TextInput,
  Spinner,
  ResponsiveContext,
} from "grommet";
import {
  Refresh,
  Search,
  StatusGood,
  Alert,
  Clock,
  Home,
  Edit,
} from "grommet-icons";
import moment from "moment";
import DoorService from "@services/door.service";
import HotelService from "@services/hotel.service";
import { useFetchHotel } from "./redux/hooks";
import { presentToastSuccess, presentToastErrorContent } from "@shared/Toast";
import { useTranslation } from "react-i18next";

// INSPECTED tab is hidden for now — may be re-added later.
const STATUS_TABS = [
  { key: "ALL", labelKey: "housekeeping.allRooms" },
  { key: "DIRTY", labelKey: "housekeeping.dirty" },
  { key: "CLEAN", labelKey: "housekeeping.clean" },
  // { key: "INSPECTED", labelKey: "housekeeping.inspected" },
];

const STATUS_THEME = {
  CLEAN: { color: "#16a34a", bg: "#ecfdf5", labelKey: "housekeeping.clean" },
  DIRTY: { color: "#d97706", bg: "#fff7ed", labelKey: "housekeeping.dirty" },
  INSPECTED: {
    color: "#2563eb",
    bg: "#eff6ff",
    labelKey: "housekeeping.inspected",
  },
  NOT_AVAILABLE: {
    color: "#dc2626",
    bg: "#fef2f2",
    labelKey: "housekeeping.unavailable",
  },
  DEFAULT: {
    color: "#64748b",
    bg: "#f1f5f9",
    labelKey: "housekeeping.unknown",
  },
};

const themeFor = (status) => STATUS_THEME[status] || STATUS_THEME.DEFAULT;

function StatusPill({ status }) {
  const { t } = useTranslation();
  const theme = themeFor(status);
  return (
    <Box
      pad={{ horizontal: "small", vertical: "4px" }}
      round="large"
      background={theme.bg}
      style={{
        display: "inline-flex",
        alignSelf: "flex-start",
        border: `1px solid ${theme.color}33`,
      }}
    >
      <Text size="xsmall" weight="bold" color={theme.color}>
        {t(theme.labelKey).toUpperCase()}
      </Text>
    </Box>
  );
}

function KpiCard({ icon, label, value, accent }) {
  return (
    <Box
      pad="medium"
      round="medium"
      background="white"
      flex="grow"
      basis="0"
      gap="xsmall"
      style={{
        boxShadow: "0 1px 3px rgba(15,23,42,0.06)",
        border: "1px solid #e2e8f0",
        minWidth: "140px",
      }}
    >
      <Box direction="row" align="center" gap="small">
        <Box
          pad="6px"
          round="small"
          style={{ background: `${accent}1a`, color: accent }}
        >
          {icon}
        </Box>
        <Text size="small" color="dark-4">
          {label}
        </Text>
      </Box>
      <Text size="32px" weight="bold" color="dark-1">
        {value}
      </Text>
    </Box>
  );
}

function FilterPill({ active, label, count, onClick, accent }) {
  return (
    <Button plain onClick={onClick}>
      <Box
        direction="row"
        align="center"
        gap="xsmall"
        pad={{ horizontal: "small", vertical: "8px" }}
        round="large"
        background={active ? accent : "white"}
        style={{
          border: `1px solid ${active ? accent : "#e2e8f0"}`,
          transition: "all 120ms ease",
        }}
      >
        <Text size="small" weight="bold" color={active ? "white" : "dark-2"}>
          {label}
        </Text>
        <Box
          pad={{ horizontal: "6px" }}
          round="small"
          background={active ? "rgba(255,255,255,0.25)" : "#f1f5f9"}
        >
          <Text size="xsmall" color={active ? "white" : "dark-3"}>
            {count}
          </Text>
        </Box>
      </Box>
    </Button>
  );
}

function RoomRow({ door, onSetStatus, isLast }) {
  const { t } = useTranslation();
  const theme = themeFor(door.status);
  const cleaned = door.lastCleanedAt;
  const dirty = door.lastDirtyAt;
  return (
    <Box
      direction="row"
      align="center"
      pad={{ horizontal: "medium", vertical: "small" }}
      gap="medium"
      style={{
        borderBottom: isLast ? "none" : "1px solid #e2e8f0",
        borderLeft: `3px solid ${theme.color}`,
      }}
    >
      {/* Room name */}
      <Box direction="row" align="center" gap="xsmall" width="180px">
        <Home size="16px" color="#94a3b8" />
        <Text weight="bold" color="dark-1">
          {door.name}
        </Text>
      </Box>

      {/* Status */}
      <Box width="110px">
        <StatusPill status={door.status} />
      </Box>

      {/* Last dirty */}
      <Box
        direction="row"
        align="center"
        gap="xsmall"
        width="180px"
        title={
          dirty
            ? `Dirty · ${moment(dirty).format("MMM D, YYYY HH:mm")}`
            : undefined
        }
      >
        <Clock size="14px" color="#cbd5e1" />
        <Text size="small" color={dirty ? "dark-3" : "dark-5"}>
          {dirty ? moment(dirty).fromNow() : "—"}
        </Text>
      </Box>

      {/* Last cleaned */}
      <Box
        direction="row"
        align="center"
        gap="xsmall"
        flex="grow"
        title={
          cleaned
            ? `Cleaned · ${moment(cleaned).format("MMM D, YYYY HH:mm")}${
                door.lastCleanedSource ? ` · ${door.lastCleanedSource}` : ""
              }`
            : undefined
        }
      >
        <StatusGood size="14px" color="#cbd5e1" />
        <Text size="small" color={cleaned ? "dark-3" : "dark-5"} truncate>
          {cleaned ? moment(cleaned).fromNow() : "—"}
        </Text>
      </Box>

      {/* Actions */}
      <Box direction="row" gap="xsmall">
        <Button
          size="small"
          primary
          color="#16a34a"
          label={t("housekeeping.cleanButton")}
          onClick={() => onSetStatus(door._id, "CLEAN")}
          disabled={door.status === "CLEAN"}
        />
        <Button
          size="small"
          color="#d97706"
          label={t("housekeeping.dirtyButton")}
          onClick={() => onSetStatus(door._id, "DIRTY")}
          disabled={door.status === "DIRTY"}
        />
      </Box>
    </Box>
  );
}

function RoomRowMobile({ door, onSetStatus, isLast }) {
  const { t } = useTranslation();
  const theme = themeFor(door.status);
  const cleaned = door.lastCleanedAt;
  const dirty = door.lastDirtyAt;
  return (
    <Box
      pad="small"
      gap="xsmall"
      style={{
        borderBottom: isLast ? "none" : "1px solid #e2e8f0",
        borderLeft: `3px solid ${theme.color}`,
      }}
    >
      <Box direction="row" justify="between" align="center">
        <Box direction="row" align="center" gap="xsmall">
          <Home size="16px" color="#94a3b8" />
          <Text weight="bold">{door.name}</Text>
        </Box>
        <StatusPill status={door.status} />
      </Box>
      {(dirty || cleaned) && (
        <Box gap="2px">
          {dirty && (
            <Text
              size="xsmall"
              color="dark-4"
              title={`Dirty · ${moment(dirty).format("MMM D, YYYY HH:mm")}`}
            >
              {t("housekeeping.dirty")} {moment(dirty).fromNow()}
            </Text>
          )}
          {cleaned && (
            <Text
              size="xsmall"
              color="dark-4"
              title={`Cleaned · ${moment(cleaned).format("MMM D, YYYY HH:mm")}${
                door.lastCleanedSource ? ` · ${door.lastCleanedSource}` : ""
              }`}
            >
              {t("housekeeping.clean")} {moment(cleaned).fromNow()}
            </Text>
          )}
        </Box>
      )}
      <Box direction="row" gap="xsmall">
        <Button
          size="small"
          primary
          color="#16a34a"
          label={t("housekeeping.cleanButton")}
          onClick={() => onSetStatus(door._id, "CLEAN")}
          disabled={door.status === "CLEAN"}
        />
        <Button
          size="small"
          color="#d97706"
          label={t("housekeeping.dirtyButton")}
          onClick={() => onSetStatus(door._id, "DIRTY")}
          disabled={door.status === "DIRTY"}
        />
      </Box>
    </Box>
  );
}

export default function HousekeepingPage() {
  const { t } = useTranslation();
  const { hotel, fetchHotel } = useFetchHotel();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("ALL");
  const [search, setSearch] = useState("");
  const [lastFetched, setLastFetched] = useState(null);
  const [editingCode, setEditingCode] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [savingCode, setSavingCode] = useState(false);

  const fetchDoors = async () => {
    setLoading(true);
    try {
      const res = await DoorService.getDoors();
      const onlyRooms = (res.data || []).filter(
        (d) => d.type === "room" && !d.disabled && d.lockId,
      );
      setRooms(onlyRooms);
      setLastFetched(new Date());
    } catch (e) {
      presentToastErrorContent(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDoors();
  }, []);

  const handleSaveCode = async () => {
    const trimmed = codeInput.trim();
    if (!trimmed) return;
    setSavingCode(true);
    try {
      await HotelService.updateHotel({ cleaningCode: trimmed });
      await fetchHotel();
      presentToastSuccess("Cleaning code updated");
      setEditingCode(false);
    } catch (e) {
      presentToastErrorContent(e);
    } finally {
      setSavingCode(false);
    }
  };

  const handleSetStatus = async (doorId, status) => {
    try {
      const res = await DoorService.setCleaningStatus(doorId, status);
      setRooms((prev) =>
        prev.map((r) => (r._id === doorId ? { ...r, ...res.data } : r)),
      );
      presentToastSuccess("Status updated");
    } catch (e) {
      presentToastErrorContent(e);
    }
  };

  const counts = useMemo(() => {
    const c = { ALL: rooms.length, DIRTY: 0, CLEAN: 0, INSPECTED: 0, OTHER: 0 };
    rooms.forEach((r) => {
      if (c[r.status] != null) c[r.status]++;
      else c.OTHER++;
    });
    return c;
  }, [rooms]);

  const filtered = useMemo(() => {
    let list = rooms;
    if (activeTab !== "ALL") list = list.filter((r) => r.status === activeTab);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((r) => (r.name || "").toLowerCase().includes(q));
    }
    return list.slice().sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, {
        numeric: true,
      }),
    );
  }, [rooms, activeTab, search]);

  return (
    <ResponsiveContext.Consumer>
      {(size) => (
        <Box
          pad={{ horizontal: "medium", vertical: "medium" }}
          gap="medium"
          flex="grow"
          margin={{ bottom: "300px" }}
          width={{ max: "xlarge", width: "100%" }}
          alignSelf="center"
        >
          {/* Header */}
          <Box
            direction={size === "small" ? "column" : "row"}
            justify="between"
            align={size === "small" ? "start" : "center"}
            gap="small"
          >
            <Box gap="4px">
              <Heading level="2" margin="none">
                {t("housekeeping.title")}
              </Heading>
              <Text size="small" color="dark-4">
                {t("housekeeping.subtitle")}
                {lastFetched && (
                  <>
                    {" · "}updated {moment(lastFetched).fromNow()}
                  </>
                )}
              </Text>
              {editingCode ? (
                <Box direction="row" align="center" gap="xsmall">
                  <Text size="small" color="dark-4">
                    {t("housekeeping.cleaningCode")}
                  </Text>
                  <TextInput
                    plain
                    value={codeInput}
                    onChange={(e) => setCodeInput(e.target.value)}
                    size="small"
                    style={{
                      width: 100,
                      border: "1px solid #e2e8f0",
                      borderRadius: 6,
                      padding: "2px 8px",
                    }}
                  />
                  <Text size="small" color="dark-4">
                    #
                  </Text>
                  <Button
                    size="small"
                    primary
                    label={savingCode ? t("common.loading") : t("common.save")}
                    onClick={handleSaveCode}
                    disabled={savingCode || !codeInput.trim()}
                  />
                  <Button
                    size="small"
                    label={t("common.cancel")}
                    onClick={() => setEditingCode(false)}
                  />
                </Box>
              ) : (
                <Box direction="row" align="center" gap="xsmall">
                  <Text size="small" color="dark-4">
                    {t("housekeeping.cleaningCode")}{" "}
                    <strong>{hotel?.cleaningCode || "14753"}#</strong>
                  </Text>
                  <Button
                    plain
                    icon={<Edit size="14px" color="#94a3b8" />}
                    onClick={() => {
                      setCodeInput(hotel?.cleaningCode || "14753");
                      setEditingCode(true);
                    }}
                  />
                </Box>
              )}
            </Box>
            <Button
              icon={loading ? <Spinner size="xsmall" /> : <Refresh />}
              label={loading ? t("common.loading") : t("unlockRecords.refresh")}
              onClick={fetchDoors}
              disabled={loading}
            />
          </Box>

          {/* KPI tiles */}
          <Box direction="row" gap="small" wrap style={{ rowGap: 8 }}>
            <KpiCard
              icon={<Home size="18px" />}
              label={t("housekeeping.allRooms")}
              value={counts.ALL}
              accent="#0ea5e9"
            />
            <KpiCard
              icon={<Alert size="18px" />}
              label={t("housekeeping.dirty")}
              value={counts.DIRTY}
              accent="#d97706"
            />
            <KpiCard
              icon={<StatusGood size="18px" />}
              label={t("housekeeping.clean")}
              value={counts.CLEAN}
              accent="#16a34a"
            />
          </Box>

          {/* Filter + search bar */}
          <Box
            direction={size === "small" ? "column" : "row"}
            gap="small"
            justify="between"
            align={size === "small" ? "stretch" : "center"}
          >
            <Box direction="row" gap="xsmall" wrap style={{ rowGap: 8 }}>
              {STATUS_TABS.map((tab) => (
                <FilterPill
                  key={tab.key}
                  active={activeTab === tab.key}
                  label={t(tab.labelKey)}
                  count={counts[tab.key] ?? 0}
                  accent={
                    tab.key === "DIRTY"
                      ? "#d97706"
                      : tab.key === "CLEAN"
                        ? "#16a34a"
                        : "#0f172a"
                  }
                  onClick={() => setActiveTab(tab.key)}
                />
              ))}
            </Box>
            <Box
              direction="row"
              align="center"
              gap="xsmall"
              pad={{ horizontal: "small" }}
              round="small"
              background="white"
              style={{
                border: "1px solid #e2e8f0",
                width: size === "small" ? "100%" : "260px",
              }}
            >
              <Search size="16px" color="#94a3b8" />
              <TextInput
                plain
                placeholder={t("housekeeping.searchPlaceholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Box>
          </Box>

          {/* Room grid */}
          {loading && rooms.length === 0 ? (
            <Box align="center" pad="large">
              <Spinner />
            </Box>
          ) : filtered.length === 0 ? (
            <Box
              align="center"
              justify="center"
              pad="large"
              round="medium"
              background="white"
              style={{ border: "1px dashed #cbd5e1" }}
              gap="xsmall"
            >
              <Text weight="bold" color="dark-2">
                {t("housekeeping.noRooms")}
              </Text>
              <Text size="small" color="dark-4">
                {t("housekeeping.noRoomsDesc")}
              </Text>
            </Box>
          ) : (
            <Box
              round="medium"
              background="white"
              style={{
                border: "1px solid #e2e8f0",
                boxShadow: "0 1px 3px rgba(15,23,42,0.05)",
                overflow: "hidden",
              }}
            >
              {/* List header (desktop only) */}
              {size !== "small" && (
                <Box
                  direction="row"
                  align="center"
                  pad={{ horizontal: "medium", vertical: "xsmall" }}
                  gap="medium"
                  background="#f8fafc"
                  style={{ borderBottom: "1px solid #e2e8f0" }}
                >
                  <Text
                    size="xsmall"
                    color="dark-4"
                    weight="bold"
                    style={{ width: 180 }}
                  >
                    {t("housekeeping.colRoom")}
                  </Text>
                  <Text
                    size="xsmall"
                    color="dark-4"
                    weight="bold"
                    style={{ width: 110 }}
                  >
                    {t("housekeeping.colStatus")}
                  </Text>
                  <Text
                    size="xsmall"
                    color="dark-4"
                    weight="bold"
                    style={{ width: 180 }}
                  >
                    {t("housekeeping.colLastDirty")}
                  </Text>
                  <Text
                    size="xsmall"
                    color="dark-4"
                    weight="bold"
                    style={{ flex: 1 }}
                  >
                    {t("housekeeping.colLastCleaned")}
                  </Text>
                  <Text size="xsmall" color="dark-4" weight="bold">
                    {t("housekeeping.colActions")}
                  </Text>
                </Box>
              )}

              {filtered.map((door, i) =>
                size === "small" ? (
                  <RoomRowMobile
                    key={door._id}
                    door={door}
                    onSetStatus={handleSetStatus}
                    isLast={i === filtered.length - 1}
                  />
                ) : (
                  <RoomRow
                    key={door._id}
                    door={door}
                    onSetStatus={handleSetStatus}
                    isLast={i === filtered.length - 1}
                  />
                ),
              )}
            </Box>
          )}
        </Box>
      )}
    </ResponsiveContext.Consumer>
  );
}
